/**
 * Utility functions for handling German number format
 */

/**
 * Convert German decimal format (comma) to international format (dot)
 * @param value - Input value with comma as decimal separator
 * @returns Value with dot as decimal separator
 */
export function replaceCommaWithDot(value: string): string {
  return value.replace(',', '.')
}

/**
 * Convert international format (dot) to German decimal format (comma)
 * @param value - Input value with dot as decimal separator
 * @returns Value with comma as decimal separator
 */
export function replaceDotWithComma(value: string | number): string {
  return String(value).replace('.', ',')
}

/**
 * Restrict input to valid German decimal format
 * Allows digits and one comma, with configurable limits
 * @param value - Current input value
 * @param maxDigitsBeforeComma - Maximum digits before comma (default: 3)
 * @param maxDigitsAfterComma - Maximum digits after comma (default: 2)
 * @returns Restricted value
 */
export function restrictDecimalInput(
  value: string,
  maxDigitsBeforeComma: number = 3,
  maxDigitsAfterComma: number = 2
): string {
  // Remove all non-digit, non-comma characters
  let restricted = value.replace(/[^0-9,]/g, '')
  
  // Remove extra commas (only one allowed)
  const commaIndex = restricted.indexOf(',')
  if (commaIndex !== -1) {
    const beforeComma = restricted.substring(0, commaIndex)
    const afterComma = restricted.substring(commaIndex + 1).replace(/,/g, '')
    restricted = beforeComma + ',' + afterComma
  }
  
  // Apply digit limits
  if (restricted.includes(',')) {
    const parts = restricted.split(',')
    parts[0] = parts[0].substring(0, maxDigitsBeforeComma)
    parts[1] = parts[1].substring(0, maxDigitsAfterComma)
    restricted = parts.join(',')
  } else {
    restricted = restricted.substring(0, maxDigitsBeforeComma)
  }
  
  return restricted
}

/**
 * Validate if a value respects maximum limit
 * @param value - Value to validate (with comma or dot)
 * @param maxValue - Maximum allowed value
 * @returns True if valid, false otherwise
 */
export function validateMaxValue(value: string, maxValue: number): boolean {
  const numericValue = parseFloat(replaceCommaWithDot(value))
  return !isNaN(numericValue) && numericValue <= maxValue
}

/**
 * Format a number for display in German format
 * @param value - Numeric value
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string with comma as decimal separator
 */
export function formatGermanDecimal(value: number | string, decimals: number = 2): string {
  const numericValue = typeof value === 'string' ? parseFloat(replaceCommaWithDot(value)) : value
  
  if (isNaN(numericValue)) {
    return '0,00'
  }
  
  return numericValue.toFixed(decimals).replace('.', ',')
}

/**
 * Parse German decimal input to numeric value
 * @param value - Input with comma as decimal separator
 * @returns Parsed float value or null if invalid
 */
export function parseGermanDecimal(value: string): number | null {
  if (!value || value.trim() === '') {
    return null
  }
  
  const parsed = parseFloat(replaceCommaWithDot(value))
  return isNaN(parsed) ? null : parsed
}

/**
 * Service-specific validation configuration
 */
export interface ServiceValidation {
  maxValue?: number
  maxDigitsBeforeComma?: number
  maxDigitsAfterComma?: number
  required?: boolean
  unit?: string
}

/**
 * Get validation rules for specific service types
 * @param serviceType - Type or name of the service
 * @returns Validation configuration
 */
export function getServiceValidation(serviceType: string): ServiceValidation {
  const serviceValidations: Record<string, ServiceValidation> = {
    'Tanken': {
      maxValue: 99.99,
      maxDigitsBeforeComma: 2,
      maxDigitsAfterComma: 2,
      required: true,
      unit: 'Liter'
    },
    'Fuel': {
      maxValue: 99.99,
      maxDigitsBeforeComma: 2,
      maxDigitsAfterComma: 2,
      required: true,
      unit: 'Liter'
    },
    'Kilometerstand': {
      maxValue: 999999,
      maxDigitsBeforeComma: 6,
      maxDigitsAfterComma: 0,
      required: true,
      unit: 'km'
    },
    'Mileage': {
      maxValue: 999999,
      maxDigitsBeforeComma: 6,
      maxDigitsAfterComma: 0,
      required: true,
      unit: 'km'
    },
    'default': {
      maxValue: 9999.99,
      maxDigitsBeforeComma: 4,
      maxDigitsAfterComma: 2,
      required: false
    }
  }
  
  return serviceValidations[serviceType] || serviceValidations['default']
}